import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { X, ExternalLink, Loader2, ShieldOff, Sparkles } from "lucide-react";
import { api } from "../lib/api";

/**
 * ProfileDialog — lets the user choose what name shows in the UI.
 * The real account name (used for billing / records) is never changed; only
 * the `display_name` shown in the header & greetings.
 */
export function ProfileDialog({ open, onClose }) {
  const { user, updateDisplayName } = useAuth();
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [history, setHistory] = useState(null);

  const isMonthly = user && typeof user === "object" && user.tier === "monthly";
  const isLifetime = user && typeof user === "object" && user.tier === "lifetime";
  const isPaid = isMonthly || isLifetime;

  useEffect(() => {
    if (open && user && typeof user === "object") {
      const t = setTimeout(() => setValue(user.display_name || ""), 0);
      return () => clearTimeout(t);
    }
  }, [open, user]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);


  useEffect(() => {
    if (!open || !isPaid) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/billing/history");
        if (!cancelled) setHistory(data?.items || []);
      } catch {
        if (!cancelled) setHistory([]);
      }
    }, 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, isPaid]);

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.origin + "/vault" });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not open billing portal.");
        setPortalLoading(false);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not open billing portal.");
      setPortalLoading(false);
    }
  };

  if (!open || !user || typeof user !== "object") return null;

  const handleSave = async () => {
    setSaving(true);
    const res = await updateDisplayName(value);
    setSaving(false);
    if (res.ok) {
      toast.success("Display name updated.");
      onClose();
    } else {
      toast.error(res.error || "Could not update display name.");
    }
  };

  const handleClear = async () => {
    setSaving(true);
    const res = await updateDisplayName("");
    setSaving(false);
    if (res.ok) {
      toast.success("Display name cleared. You'll appear as 'Member' now.");
      onClose();
    } else {
      toast.error(res.error || "Could not clear display name.");
    }
  };

  // Portal to <body>: the sticky header's backdrop-filter would otherwise trap this fixed overlay inside the header box.
  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="profile-dialog"
    >
      <div
        className="relative w-full max-w-md max-h-full overflow-y-auto bg-[#0a0a0a] border border-purple-500/30 p-8 md:p-10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-zinc-500 hover:text-purple-300 transition-colors"
          aria-label="Close"
          data-testid="profile-dialog-close"
        >
          <X size={18} />
        </button>

        <div className="text-purple-400 text-sm tracking-[0.25em] uppercase mb-3">
          Profile
        </div>
        <h2 className="font-display text-3xl font-bold tracking-tight mb-2">
          Display name
        </h2>
        <p className="text-zinc-400 text-base leading-relaxed mb-8">
          This is the name shown in the header and on your dashboard. Leave it
          blank to appear simply as <span className="text-zinc-300">Member</span>.
          Your account name stays private.
        </p>

        <label className="block text-zinc-400 text-sm tracking-wide mb-2">
          Show me as
        </label>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="e.g. Stag Builder, AB, or leave blank"
          maxLength={60}
          className="w-full bg-black border border-zinc-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none px-4 py-3 text-base text-white placeholder:text-zinc-700 transition-all"
          data-testid="profile-display-name-input"
          autoFocus
        />
        <p className="text-zinc-600 text-sm mt-2">
          {value.length}/60 characters.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-purple-500 hover:bg-purple-400 text-black px-6 py-3 text-sm tracking-[0.2em] uppercase transition-all duration-300 shadow-[0_0_20px_rgba(168,85,247,0.25)] hover:shadow-[0_0_35px_rgba(168,85,247,0.6)] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="profile-save-button"
          >
            {saving ? "Saving..." : "Save"}
          </button>
          <button
            onClick={handleClear}
            disabled={saving || !user.display_name}
            className="px-6 py-3 border border-zinc-800 hover:border-purple-500 text-zinc-300 hover:text-purple-300 text-sm tracking-[0.2em] uppercase transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="profile-clear-button"
          >
            Clear
          </button>
        </div>

        <div className="mt-8 pt-6 border-t border-zinc-900 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-zinc-500 text-sm">Plan</span>
            <span className="text-sm" data-testid="profile-tier">
              {isLifetime && <span className="text-purple-300 tracking-wide">Lifetime Access</span>}
              {isMonthly && <span className="text-purple-300 tracking-wide">Stag Vault Complete — Monthly</span>}
              {!isLifetime && !isMonthly && <span className="text-zinc-400">Free</span>}
            </span>
          </div>
          {isMonthly && (
            <button
              onClick={openBillingPortal}
              disabled={portalLoading}
              className="w-full inline-flex items-center justify-between border border-zinc-800 hover:border-purple-500/60 text-zinc-300 hover:text-purple-300 px-4 py-3 text-xs tracking-[0.25em] uppercase font-bold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="profile-billing-portal-button"
            >
              <span className="inline-flex items-center gap-2">
                {portalLoading ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />}
                {portalLoading ? "Opening portal..." : "Manage subscription"}
              </span>
              <span className="text-zinc-600 text-[10px] normal-case tracking-wide">Update card · invoices</span>
            </button>
          )}
          {isMonthly && (
            <Link
              to="/upgrade"
              onClick={onClose}
              className="w-full inline-flex items-center justify-between border border-purple-500/30 hover:border-purple-500/80 bg-purple-500/[0.04] hover:bg-purple-500/[0.08] text-purple-200 hover:text-purple-100 px-4 py-3 text-xs tracking-[0.25em] uppercase font-bold transition-all duration-300 shadow-[0_0_15px_rgba(168,85,247,0.1)] hover:shadow-[0_0_25px_rgba(168,85,247,0.3)]"
              data-testid="profile-upgrade-to-lifetime-link"
            >
              <span className="inline-flex items-center gap-2">
                <Sparkles size={13} />
                Upgrade to Lifetime
              </span>
              <span className="text-purple-400/70 text-[10px] normal-case tracking-wide">Prorated · all future drops</span>
            </Link>
          )}
          {isMonthly && (
            <Link
              to="/billing/cancel"
              onClick={onClose}
              className="w-full inline-flex items-center justify-between border border-zinc-900 hover:border-red-500/50 text-zinc-500 hover:text-red-400 px-4 py-3 text-xs tracking-[0.25em] uppercase font-bold transition-all duration-300"
              data-testid="profile-cancel-subscription-link"
            >
              <span className="inline-flex items-center gap-2">
                <ShieldOff size={13} />
                Cancel or pause
              </span>
              <span className="text-zinc-700 text-[10px] normal-case tracking-wide">30-day pause available</span>
            </Link>
          )}
          {isPaid && history && history.length > 0 && (
            <div className="pt-2" data-testid="profile-billing-history">
              <div className="text-zinc-500 text-xs tracking-[0.25em] uppercase mb-3 font-mono">
                Billing history
              </div>
              <ul className="space-y-1.5">
                {history.slice(0, 5).map((h) => {
                  const paid = h.payment_status === "paid";
                  const amount = typeof h.amount === "number" ? h.amount.toFixed(2) : h.amount;
                  const currencySym = (h.currency || "gbp").toLowerCase() === "gbp" ? "£" : "$";
                  const date = h.created_at ? new Date(h.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "";
                  const planLabel = h.plan === "monthly" ? "Monthly" : "Lifetime";
                  return (
                    <li
                      key={h.id}
                      className="flex items-center justify-between text-xs gap-3 py-1.5 border-b border-zinc-900 last:border-b-0"
                      data-testid={`profile-billing-history-item-${h.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${paid ? "bg-purple-400" : "bg-zinc-700"}`} />
                        <span className="text-zinc-300">{planLabel}</span>
                        <span className="text-zinc-600">·</span>
                        <span className="text-zinc-500">{date}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-zinc-200 font-mono">{currencySym}{amount}</span>
                        <span className={`text-[10px] tracking-[0.2em] uppercase ${paid ? "text-purple-400" : "text-zinc-500"}`}>
                          {paid ? "Paid" : (h.payment_status || h.status || "Pending")}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {history.length > 5 && (
                <div className="text-zinc-600 text-[11px] tracking-wide mt-2">
                  Showing 5 most recent · {history.length} total
                </div>
              )}
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-zinc-500 text-sm">Email</span>
            <span className="text-zinc-300 text-sm" data-testid="profile-email">
              {user.email}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-zinc-500 text-sm">Account name</span>
            <span className="text-zinc-500 text-sm italic">
              (private — never shown)
            </span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
